import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import ChangeRequestForm from '../components/Forms/ChangeRequestForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../services/NotificationService';

const { FiPlus, FiEdit, FiTrash2, FiGitPullRequest, FiClock, FiAlertCircle } = FiIcons;

const ChangeRequests = () => {
  const { 
    changeRequests, contracts, clients, users, settings,
    addChangeRequest, updateChangeRequest, deleteChangeRequest 
  } = useCRM();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);

  const handleAddRequest = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setIsModalOpen(true);
  };

  const handleSubmit = async (data) => {
    if (editingRequest) {
      await updateChangeRequest(editingRequest.id, data);
      
      // Notify on status update
      if (editingRequest.status !== data.status) {
        // Pass FULL request object so service can access createdBy
        const contract = contracts.find(c => c.id === data.contractId);
        const fullRequest = { ...editingRequest, ...data };
        await NotificationService.sendChangeRequestStatusUpdateEmail(fullRequest, contract, settings);
      }
    } else {
      // Create new request
      await addChangeRequest(data);
      
      // Send notification to admins
      const contract = contracts.find(c => c.id === data.contractId);
      const client = clients.find(c => c.id === contract?.clientId);
      const admins = users.filter(u => u.role === 'Admin');
      
      if (contract) {
        await NotificationService.sendChangeRequestCreatedEmail(data, contract, client, admins, settings);
      }
    }
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const handleDeleteRequest = (id) => {
    if (window.confirm('Are you sure you want to delete this change request?')) {
      deleteChangeRequest(id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Change Requests</h1>
        <Button onClick={handleAddRequest}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Request
        </Button>
      </div>

      {changeRequests.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiGitPullRequest} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No change requests yet</h3>
          <p className="text-gray-500 mb-4">Track change requests for your contracts.</p>
          <Button onClick={handleAddRequest}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Request
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {changeRequests.map((request, index) => {
            const contract = contracts.find(c => c.id === request.contractId);
            const client = clients.find(c => c.id === contract?.clientId);
            return (
              <motion.div 
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {request.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {contract?.title} - {client?.name}
                      </p>
                    </div>
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => handleEditRequest(request)}
                        className="text-gray-400 hover:text-primary-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteRequest(request.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(request.priority)}`}>
                      {request.priority} priority
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiClock} className="h-4 w-4 mr-2" />
                      <span>
                        Requested on {format(new Date(request.requestDate), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    {request.estimatedHours && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiAlertCircle} className="h-4 w-4 mr-2" />
                        <span>Estimated: {request.estimatedHours} hours</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Description</h4>
                    <p className="text-sm text-gray-600 line-clamp-3">{request.description}</p>
                  </div>

                  {request.notes && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Notes</h4>
                      <p className="text-sm text-gray-600">{request.notes}</p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Created {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRequest ? 'Edit Change Request' : 'Add New Change Request'}
        size="xl"
      >
        <ChangeRequestForm 
          changeRequest={editingRequest} 
          onSubmit={handleSubmit} 
          onCancel={() => setIsModalOpen(false)} 
        />
      </Modal>
    </div>
  );
};

export default ChangeRequests;